import moment from "moment";
import { getInsuranceProviderLabel } from "./insuranceProviders";

const pickNonEmptyField = (...values) => {
  for (const value of values) {
    if (value === undefined || value === null) {
      continue;
    }
    const normalized = String(value).trim();
    if (normalized) {
      return normalized;
    }
  }
  return "";
};

const formatCopayAmount = (value) => {
  const num = Number(value);
  if (Number.isNaN(num)) {
    return null;
  }
  return `$${num.toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
};

export const hasEstimatedCopayFields = (eligibilityResult) => {
  if (!eligibilityResult || typeof eligibilityResult !== "object") {
    return false;
  }

  return (
    Object.prototype.hasOwnProperty.call(
      eligibilityResult,
      "estimated_copay_min"
    ) ||
    Object.prototype.hasOwnProperty.call(
      eligibilityResult,
      "estimated_copay_max"
    ) ||
    Object.prototype.hasOwnProperty.call(
      eligibilityResult,
      "estimatedCopayMin"
    ) ||
    Object.prototype.hasOwnProperty.call(
      eligibilityResult,
      "estimatedCopayMax"
    )
  );
};

export const resolveEstimatedCopayAmounts = (eligibilityResult) => {
  if (!hasEstimatedCopayFields(eligibilityResult)) {
    return null;
  }

  const minRaw =
    eligibilityResult.estimated_copay_min ??
    eligibilityResult.estimatedCopayMin;
  const maxRaw =
    eligibilityResult.estimated_copay_max ??
    eligibilityResult.estimatedCopayMax;

  const min =
    minRaw === null || minRaw === undefined ? 0 : Number(minRaw);
  const max =
    maxRaw === null || maxRaw === undefined ? 0 : Number(maxRaw);

  return {
    min: Number.isNaN(min) ? 0 : min,
    max: Number.isNaN(max) ? 0 : max,
  };
};

export const formatEstimatedCopayRange = (eligibilityResult) => {
  const amounts = resolveEstimatedCopayAmounts(eligibilityResult);
  if (!amounts) {
    return null;
  }

  const minFormatted = formatCopayAmount(amounts.min);
  const maxFormatted = formatCopayAmount(amounts.max);

  if (!minFormatted || !maxFormatted) {
    return null;
  }

  if (minFormatted === maxFormatted) {
    return minFormatted;
  }

  return `${minFormatted} – ${maxFormatted}`;
};

export const formatCoverageSinceDisplay = (value) => {
  if (value === undefined || value === null || value === "") {
    return null;
  }

  const raw = String(value).trim();
  const strictParsed = moment(
    raw,
    [
      "YYYYMMDD",
      "YYYY-MM-DD",
      "YYYY/MM/DD",
      "MM/DD/YYYY",
      "MMM D, YYYY",
      moment.ISO_8601,
    ],
    true
  );

  if (strictParsed.isValid()) {
    return strictParsed.format("MMM D, YYYY");
  }

  const looseParsed = moment(raw);
  if (looseParsed.isValid()) {
    return looseParsed.format("MMM D, YYYY");
  }

  return raw;
};

const formatCoverageStatusDisplay = (value) => {
  if (value === undefined || value === null || value === "") {
    return null;
  }
  const normalized = String(value).trim();
  if (!normalized) {
    return null;
  }
  return normalized.charAt(0).toUpperCase() + normalized.slice(1).toLowerCase();
};

export const buildCopayEstimateDisclaimer = (planType) => {
  const normalized = typeof planType === "string" ? planType.trim() : "";
  const planPhrase = normalized
    ? `Based on your ${normalized} plan.`
    : "Based on your plan.";

  return `${planPhrase} Exact amount depends on whether your deductible has been met. Final cost confirmed at time of service.`;
};

export const buildCoverageResultFromEligibility = (
  eligibilityResult,
  verification,
  manual,
  providersList
) => {
  if (!eligibilityResult || typeof eligibilityResult !== "object") {
    return null;
  }

  const payerName = pickNonEmptyField(
    eligibilityResult.payer_name,
    eligibilityResult.payerName,
    manual?.provider,
    verification?.providerName,
    getInsuranceProviderLabel(verification?.payer, providersList)
  );

  const copayEstimate = pickNonEmptyField(
    formatEstimatedCopayRange(eligibilityResult),
    eligibilityResult.copay_estimate,
    eligibilityResult.copayEstimate
  );

  return {
    payerName,
    planName: pickNonEmptyField(
      eligibilityResult.plan_name,
      eligibilityResult.planName
    ),
    memberId: pickNonEmptyField(
      eligibilityResult.member_id,
      eligibilityResult.memberId,
      manual?.memberId,
      verification?.memberId
    ),
    groupNumber: pickNonEmptyField(
      eligibilityResult.group_number,
      eligibilityResult.groupNumber,
      manual?.groupNumber,
      verification?.groupNumber
    ),
    coverageSince:
      pickNonEmptyField(
        eligibilityResult.coverage_since_display,
        eligibilityResult.coverageSinceDisplay,
        formatCoverageSinceDisplay(
          eligibilityResult.coverage_since ??
            eligibilityResult.coverageSince ??
            eligibilityResult.coverage_start_date ??
            eligibilityResult.coverageStartDate
        )
      ) || "",
    planType: pickNonEmptyField(
      eligibilityResult.plan_type,
      eligibilityResult.planType
    ),
    copayEstimate,
    copayNote: pickNonEmptyField(
      eligibilityResult.copay_note,
      eligibilityResult.copayNote
    ),
    status:
      formatCoverageStatusDisplay(
        eligibilityResult.status ?? eligibilityResult.coverage_status
      ) ?? "",
  };
};

export const DISCOVERY_CHECK_STEP_KEYS = [
  "NUTRITION_INSDISC_LBL_STEP_DEMOGRAPHICS",
  "NUTRITION_INSDISC_LBL_STEP_PAYER_NETWORK",
  "NUTRITION_INSDISC_LBL_STEP_BENEFITS",
];

export const DISCOVERY_RECHECK_COPY_KEYS = {
  titleKey: "NUTRITION_INSDISC_LBL_VERIFYING_PLAN_RECHECK",
  subtitleKey: "NUTRITION_INSDISC_DESC_CHECKING_BENEFITS",
};

export const DISCOVERY_INITIAL_COPY_KEYS = {
  titleKey: "NUTRITION_INSDISC_LBL_VERIFYING_ELIGIBILITY",
  subtitleKey: "NUTRITION_INSDISC_DESC_CHECKING_BENEFITS",
};

export const DISCOVERY_STEP_DELAYS = [500, 2000, 3500];
